import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import PathwaySelector from './components/PathwaySelector';
import QuickAssessment from './components/QuickAssessment';
import LeadMagnetSection from './components/LeadMagnetSection';
import InteractiveTools from './components/InteractiveTools';
import ProcessExplanation from './components/ProcessExplanation';
import ContactOptions from './components/ContactOptions';
import SocialProof from './components/SocialProof';

const GetStarted = () => {
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [toolResults, setToolResults] = useState(null);

  const handlePathwaySelect = (pathway) => {
    setSelectedPathway(pathway);
    // Scroll to contact form or next section
    const contactSection = document.getElementById('contact-options');
    if (contactSection) {
      contactSection?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleAssessmentComplete = (results) => {
    setAssessmentResults(results);
    // Show success message or redirect to results
    alert(`Assessment completed! Your AI readiness profile has been generated. Check your email for the detailed report.`);
  };

  const handleToolComplete = (results) => {
    setToolResults(results);
    // Show results modal or redirect
    const message = results?.type === 'readiness' 
      ? `Your AI Readiness Score: ${results?.score}/100\n\nRecommendation: ${results?.recommendation}`
      : `Your Vendor Lock-in Risk Score: ${results?.score}/100\n\nRecommendation: ${results?.recommendation}`;
    
    alert(message);
  };

  const handleDownloadBlueprint = (data) => {
    // Handle blueprint download
    alert(`AI Survival Blueprint is being sent to ${data?.email}. Check your inbox in a few minutes!`);
  };

  const handleContactSubmit = (data) => {
    // Handle contact form submission
    const methodText = data?.method === 'schedule' ? 'consultation scheduled' : 
                      data?.method === 'chat'? 'chat session initiated' : 'proposal request submitted';
    
    alert(`Thank you ${data?.data?.name}! Your ${methodText}. We'll be in touch within 24 hours.`);
  };

  const scrollToAssessment = () => {
    const assessmentSection = document.getElementById('quick-assessment');
    if (assessmentSection) {
      assessmentSection?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-options');
    if (contactSection) {
      contactSection?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero Section */}
      <HeroSection 
        onGetStarted={scrollToContact}
        onDownloadBlueprint={handleDownloadBlueprint}
      />
      {/* Pathway Selection */}
      <PathwaySelector onPathwaySelect={handlePathwaySelect} />
      {/* Quick Assessment */}
      <div id="quick-assessment">
        <QuickAssessment onAssessmentComplete={handleAssessmentComplete} />
      </div>
      {/* Lead Magnet Section */}
      <LeadMagnetSection onDownload={handleDownloadBlueprint} />
      {/* Interactive Tools */}
      <InteractiveTools onToolComplete={handleToolComplete} />
      {/* Social Proof */}
      <SocialProof />
      {/* Process Explanation */}
      <ProcessExplanation />
      {/* Contact Options */}
      <div id="contact-options">
        <ContactOptions onContactSubmit={handleContactSubmit} />
      </div>
      {/* Footer */}
      <footer className="bg-brand-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-secondary to-brand-accent rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div>
                  <div className="text-2xl font-bold">AI Clean Core</div>
                  <div className="text-sm text-blue-200">Enterprise AI Independence</div>
                </div>
              </div>
              <p className="text-blue-100 mb-6 max-w-md">
                Helping organizations break free from vendor lock-in and build sustainable AI capabilities through open-source solutions.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </a>
                <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001.012.001z"/>
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2 text-blue-200">
                <li><a href="/ai-core-blueprint" className="hover:text-white transition-colors duration-200">AI Core Blueprint</a></li>
                <li><a href="/open-source-advantage" className="hover:text-white transition-colors duration-200">Open Source Advantage</a></li>
                <li><a href="/services-implementation" className="hover:text-white transition-colors duration-200">Implementation Services</a></li>
                <li><a href="/case-studies-proof" className="hover:text-white transition-colors duration-200">Case Studies</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-blue-200">
                <li><a href="/resources-hub" className="hover:text-white transition-colors duration-200">Resource Hub</a></li>
                <li><a href="/about-ai-clean-core" className="hover:text-white transition-colors duration-200">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-200">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-200">Documentation</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-blue-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-sm">
              © {new Date()?.getFullYear()} AI Clean Core. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-blue-200 hover:text-white text-sm transition-colors duration-200">Privacy Policy</a>
              <a href="#" className="text-blue-200 hover:text-white text-sm transition-colors duration-200">Terms of Service</a>
              <a href="#" className="text-blue-200 hover:text-white text-sm transition-colors duration-200">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GetStarted;